// ═══════════════════════════════════════
// ATLAS BEASTS — ENCOUNTER MODAL
// Pokemon Go-style battle: back of char, front of opponent
// ═══════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from 'react'
import { LAUNCH_CHARACTERS } from '../../data/characters'
import { LAUNCH_CREATURES } from '../../data/creatures'
import { ELEMENTS, RARITY_CONFIG } from '../../types'
import type { Character, Creature, Element } from '../../types'
import { useToast } from '../ui/Toast'
import { ZONE_ASSETS } from '../../data/assetManifest'
import type { ZoneId as AssetZoneId } from '../../data/assetManifest'
import { playSFX, playMusic } from '../../lib/audio'
import { BattleAPI } from '../../api/client'

const SPRITE = (id: string, pose: string) => `/assets/characters/${id}/${pose}.png`
const CHAR_PORTRAITS: Record<string, string> = {
  'kai-voss':          SPRITE('kai-voss', 'back'),
  'sela-mourne':       SPRITE('sela-mourne', 'back'),
  'drego':             SPRITE('drego', 'back'),
  'vara-six':          SPRITE('vara-six', 'back'),
  'marshal-coen':      SPRITE('marshal-coen', 'back'),
  'lune':              SPRITE('lune', 'back'),
  'reef':              SPRITE('reef', 'back'),
  'oska-unmade':       SPRITE('oska-unmade', 'back'),
  'twin-daggers-yara': SPRITE('twin-daggers-yara', 'back'),
}

const ELEMENT_ICONS: Record<string, string> = {
  fire: '/assets/icons/element-fire.png',
  water: '/assets/icons/element-water.png',
  earth: '/assets/icons/element-earth.png',
  air: '/assets/icons/element-air.png',
  void: '/assets/icons/element-void.png',
  aether: '/assets/icons/element-aether.png',
}
const CP = (n: string) => `/assets/kenney/creatures/portraits/${n}.png`
const CREATURE_PORTRAITS: Record<string, string> = {
  'rell': CP('rell'), 'crag': CP('crag'), 'flick': CP('flick'),
  'warden': CP('warden'), 'orrin': CP('orrin'), 'null': CP('null'),
  'mist': CP('mist'), 'scorch': CP('scorch'), 'voss-manta': CP('voss-manta'),
  'origin-founding': CP('origin-founding'), 'snip': CP('snip'),
}

// ── Types ────────────────────────────────────────────────────────────────────

interface BountyTarget {
  id: string
  creatureName: string
  species: string
  element: Element
  rarity: string
  bountyValue: number
  requiredLevel: number
}

interface EncounterModalProps {
  bounty: BountyTarget
  onClose: () => void
  onVictory: (bountyId: string, coinsGained: number) => void
}

type EncounterPhase = 'crew-select' | 'battle' | 'victory' | 'defeat'

type MoveType = 'melee' | 'ranged' | 'special' | 'charge' | 'control' | 'burst'

interface Move {
  name: string
  type: MoveType | string
  element: Element
  power: number
  description: string
}

// ── Matchup table ────────────────────────────────────────────────────────────

const MATCHUP: Record<Element, Partial<Record<Element, number>>> = {
  fire:   { air: 2.0, void: 1.5, water: 0.5, earth: 0.75 },
  water:  { fire: 2.0, void: 1.5, earth: 0.5, air: 0.75 },
  earth:  { water: 2.0, fire: 1.5, air: 0.5, void: 0.75 },
  air:    { earth: 2.0, water: 1.5, fire: 0.5, void: 0.75 },
  void:   { aether: 2.0 },
  aether: { void: 2.0, fire: 1.5, water: 1.5, earth: 1.5, air: 1.5 },
}

function getMatchupMultiplier(attackEl: Element, defendEl: Element): number {
  return MATCHUP[attackEl]?.[defendEl] ?? 1.0
}

function getMatchupLabel(mult: number): { label: string; color: string } | null {
  if (mult >= 2.0)  return { label: '⚡ Super Effective!', color: '#39FF14' }
  if (mult >= 1.5)  return { label: '▲ Effective',        color: '#00F0FF' }
  if (mult <= 0.5)  return { label: '✕ Immune',           color: '#5A7A8A' }
  if (mult <= 0.75) return { label: '▼ Resisted',         color: '#FF4800' }
  return null
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function calcMaxHp(defense: number, baseHp = 200): number {
  return Math.round(baseHp + defense * 1.5)
}

function calcDamage(attackerAtk: number, movePower: number, defenderDef: number, mult = 1.0): number {
  const raw = (attackerAtk * movePower * mult) / 100 - defenderDef * 0.4
  return Math.max(5, Math.round(raw + Math.random() * 10 - 5))
}

function getCreatureForBounty(bounty: BountyTarget): Creature | null {
  return LAUNCH_CREATURES.find(c =>
    c.name.toLowerCase() === bounty.creatureName.toLowerCase() ||
    c.element === bounty.element
  ) ?? LAUNCH_CREATURES.find(c => c.element === bounty.element) ?? null
}

function buildCreatureMoves(creature: Creature): Move[] {
  if (creature.moves?.length) return creature.moves as Move[]
  return [
    { name: 'Wild Strike',  type: 'melee',   element: creature.element, power: 55, description: 'A basic attack.' },
    { name: 'Feral Rush',   type: 'charge',  element: creature.element, power: 70, description: 'Charges forward.' },
    { name: 'Primal Burst', type: 'special', element: creature.element, power: 90, description: 'Unleashes raw power.' },
  ]
}

// ── Sub-components ────────────────────────────────────────────────────────────

type StatusEffect = 'burned' | 'corrupted' | null

function StatusBadge({ status }: { status: StatusEffect }) {
  if (!status) return null
  const cfg = status === 'burned'
    ? { label: '🔥 Burned',    color: '#FF4800' }
    : { label: '💜 Corrupted', color: '#BF00FF' }
  return (
    <span
      className="font-display font-bold text-[10px] px-2 py-0.5 rounded-full border animate-pulse"
      style={{ borderColor: `${cfg.color}60`, color: cfg.color, background: `${cfg.color}15` }}
    >
      {cfg.label}
    </span>
  )
}

function DamageFloat({ damage, color, key: _key }: { damage: number; color: string; key?: string | number }) {
  return (
    <div
      className="absolute pointer-events-none font-display font-black text-lg z-10"
      style={{
        color,
        textShadow: `0 0 8px ${color}`,
        animation: 'dmgFloat 0.9s ease-out forwards',
        top: '10px',
        left: '50%',
        transform: 'translateX(-50%)',
      }}
    >
      -{damage}
    </div>
  )
}

function HpBar({ current, max, color }: { current: number; max: number; color: string }) {
  const pct = Math.max(0, (current / max) * 100)
  const barColor = pct > 50 ? color : pct > 20 ? '#FFB800' : '#FF3B3B'
  return (
    <div className="w-full">
      <div className="flex justify-between mb-1">
        <span className="text-label text-xs">HP</span>
        <span className="font-display font-bold text-xs text-ab-text">{current} / {max}</span>
      </div>
      <div className="h-2 rounded-full bg-white bg-opacity-10 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${pct}%`, background: barColor }}
        />
      </div>
    </div>
  )
}

function TurnLog({ entries }: { entries: string[] }) {
  return (
    <div className="card-base p-3 h-24 overflow-y-auto no-scrollbar space-y-1">
      {entries.slice().reverse().map((e, i) => (
        <p key={i} className="text-body text-xs" style={{ opacity: i === 0 ? 1 : 0.5 + (i * -0.1) }}>
          {e}
        </p>
      ))}
    </div>
  )
}

// ── Phase: Crew Select ────────────────────────────────────────────────────────

function CrewSelectPhase({
  bounty,
  onConfirm,
  onClose,
}: {
  bounty: BountyTarget
  onConfirm: (chars: Character[]) => void
  onClose: () => void
}) {
  const [selected, setSelected] = useState<string[]>([])
  const el = ELEMENTS[bounty.element]
  const rarity = RARITY_CONFIG[bounty.rarity as keyof typeof RARITY_CONFIG]

  function toggle(id: string) {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 3 ? [...prev, id] : prev
    )
  }

  function confirm() {
    if (!selected.length) return
    const chars = selected.map(id => LAUNCH_CHARACTERS.find(c => c.id === id)!).filter(Boolean)
    onConfirm(chars)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white border-opacity-5">
        <div>
          <p className="text-label text-xs mb-0.5">Hunt Target</p>
          <h2 className="font-display font-black text-lg text-ab-text">{bounty.creatureName}</h2>
          <p className="text-body text-xs">{bounty.species}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="w-14 h-14 rounded flex items-center justify-center text-3xl"
               style={{ background: `${el.color}15` }}>
            {el.emoji}
          </div>
          <span className="font-display font-bold text-xs" style={{ color: rarity?.color }}>
            {rarity?.label}
          </span>
        </div>
      </div>

      {/* Reward */}
      <div className="px-4 py-3 border-b border-white border-opacity-5 flex items-center gap-3">
        <span className="text-ab-gold font-display font-black text-sm">⚡ {bounty.bountyValue} coins</span>
        <span className="text-ab-text-dim text-xs">·</span>
        <span className="text-label text-xs">Lv.{bounty.requiredLevel}+ required</span>
      </div>

      {/* Crew picker */}
      <div className="flex-1 overflow-y-auto p-4">
        <p className="text-label text-xs mb-3">Select Crew (1–3 characters)</p>
        <div className="space-y-2">
          {LAUNCH_CHARACTERS.map(char => {
            const charEl = ELEMENTS[char.element]
            const isSelected = selected.includes(char.id)
            return (
              <button
                key={char.id}
                onClick={() => toggle(char.id)}
                className={`group flex items-center gap-2.5 w-full p-2.5 rounded-lg border transition-all duration-240 cursor-pointer text-left bg-transparent ${
                  isSelected
                    ? 'border-[#FF4800] bg-[#FF4800] bg-opacity-10'
                    : 'border-[#3A3A5A] hover:border-[#5A5A7A]'
                }`}
                style={{
                  boxShadow: isSelected ? '0 0 16px rgba(255,72,0,0.2)' : 'none',
                }}
              >
                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0"
                     style={{ background: `${charEl.color}15`, border: `1px solid ${charEl.color}30` }}>
                  <img src={CHAR_PORTRAITS[char.id]} alt={char.name}
                    className="w-full h-full object-contain character-float"
                    style={{ filter: `drop-shadow(0 0 8px ${charEl.color}60)`, imageRendering: 'crisp-edges' }}
                    onError={e => { (e.currentTarget as HTMLImageElement).style.display='none' }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[#EAF6FF] truncate uppercase" style={{ fontFamily: 'Rajdhani, system-ui' }}>{char.name}</p>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-[#5A5A7A] uppercase tracking-wider" style={{ fontFamily: 'Space Mono, monospace' }}>{char.class}</span>
                    <span className="text-[10px] text-[#3A3A5A]">•</span>
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border"
                      style={{ 
                        color: charEl.color, 
                        borderColor: `${charEl.color}40`,
                        background: `${charEl.color}10`,
                        fontFamily: 'Space Mono, monospace',
                      }}
                    >
                      <img src={ELEMENT_ICONS[char.element]} alt={charEl.label} className="w-3 h-3" />
                      {charEl.label}
                    </span>
                  </div>
                </div>
                {isSelected && (
                  <div className="w-5 h-5 rounded-full bg-[#FF4800] flex items-center justify-center flex-shrink-0 shadow-lg"
                       style={{ boxShadow: '0 0 8px rgba(255,72,0,0.5)' }}>
                    <span className="text-white text-xs font-bold">✓</span>
                  </div>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-white border-opacity-5 flex gap-3">
        <button onClick={onClose} className="btn-secondary flex-1 text-sm py-3">
          Cancel
        </button>
        <button
          onClick={confirm}
          disabled={!selected.length}
          className="btn-primary flex-2 text-sm py-3 flex-1"
          style={{ opacity: selected.length ? 1 : 0.4 }}
        >
          Hunt ({selected.length} selected)
        </button>
      </div>
    </div>
  )
}

// ── Phase: Battle ─────────────────────────────────────────────────────────────

function BattlePhase({
  crew,
  bounty,
  wildCreature,
  onVictory,
  onDefeat,
  onFlee,
}: {
  crew: Character[]
  bounty: BountyTarget
  wildCreature: Creature
  onVictory: (xp: number, coins: number) => void
  onDefeat: () => void
  onFlee: () => void
}) {
  const el = ELEMENTS[bounty.element]
  const activeChar = crew[0]
  const charEl = ELEMENTS[activeChar.element]

  const charMaxHp = calcMaxHp(activeChar.stats.defense, 180)
  const wildMaxHp = calcMaxHp(wildCreature.stats.defense, 160)

  const [charHp, setCharHp] = useState(charMaxHp)
  const [wildHp, setWildHp] = useState(wildMaxHp)
  const [log, setLog] = useState<string[]>([`${bounty.creatureName} appeared! Choose a move.`])
  const [isPlayerTurn, setIsPlayerTurn] = useState(true)
  const [isAnimating, setIsAnimating] = useState(false)
  const [matchupInfo, setMatchupInfo] = useState<{ label: string; color: string } | null>(null)
  const [charStatus, setCharStatus] = useState<StatusEffect>(null)
  const [floatDmg, setFloatDmg] = useState<{ dmg: number; color: string; key: number } | null>(null)
  const [isAttacking, setIsAttacking] = useState(false)
  const floatKey = useRef(0)

  const wildMoves = buildCreatureMoves(wildCreature)

  useEffect(() => {
    playMusic('battle-encounter')
  }, [])

  const appendLog = (msg: string) => setLog(prev => [...prev, msg])

  function showFloat(dmg: number, color: string) {
    floatKey.current += 1
    setFloatDmg({ dmg, color, key: floatKey.current })
    setTimeout(() => setFloatDmg(null), 900)
  }

  const enemyAttack = useCallback((currentCharHp: number) => {
    const move = wildMoves[Math.floor(Math.random() * wildMoves.length)]
    const mult = getMatchupMultiplier(move.element as Element, activeChar.element)
    const dmg = calcDamage(wildCreature.stats.attack, move.power * 0.7, activeChar.stats.defense, mult)
    const next = Math.max(0, currentCharHp - dmg)
    setCharHp(next)
    const info = getMatchupLabel(mult)
    if (info) setMatchupInfo(info)
    else setMatchupInfo(null)
    const hitSfx = `hit-${(move.element as Element) in { fire:1,water:1,earth:1,air:1,void:1,aether:1 } ? move.element : 'fire'}` as Parameters<typeof playSFX>[0]
    playSFX(hitSfx)
    if (mult >= 2) playSFX('super-effective')
    else if (mult <= 0.5) playSFX('not-effective')
    appendLog(`${bounty.creatureName} used ${move.name}! ${dmg} dmg${mult >= 2 ? ' (Super Effective!)' : ''}`)
    // 10% chance to apply status effect
    if (Math.random() < 0.1) {
      const s: StatusEffect = bounty.element === 'fire' ? 'burned' : bounty.element === 'void' ? 'corrupted' : null
      if (s) { setCharStatus(s); appendLog(`${activeChar.name} is ${s}!`) }
    }
    setIsAnimating(false)
    if (next <= 0) {
      playSFX('ko')
      playMusic('battle-defeat')
      setTimeout(() => onDefeat(), 600)
    } else {
      setIsPlayerTurn(true)
    }
  }, [wildCreature, activeChar, bounty.creatureName, bounty.element, wildMoves, onDefeat])

  function useMove(move: Move) {
    if (!isPlayerTurn || isAnimating) return
    setIsAnimating(true)
    setIsPlayerTurn(false)
    setIsAttacking(true)
    setMatchupInfo(null)

    const mult = getMatchupMultiplier(move.element as Element, wildCreature.element)
    const statusMult = charStatus === 'burned' ? 0.8 : charStatus === 'corrupted' ? 0.9 : 1.0
    const dmg = calcDamage(activeChar.stats.attack, move.power, wildCreature.stats.defense, mult * statusMult)
    const nextWildHp = Math.max(0, wildHp - dmg)
    
    // Attack animation
    setTimeout(() => {
      setWildHp(nextWildHp)
      showFloat(dmg, ELEMENTS[move.element].color)
      setIsAttacking(false)
    }, 300)

    const playerHitSfx = `hit-${(move.element as Element) in { fire:1,water:1,earth:1,air:1,void:1,aether:1 } ? move.element : 'fire'}` as Parameters<typeof playSFX>[0]
    playSFX(playerHitSfx)
    if (mult >= 2) { playSFX('super-effective'); setTimeout(() => playSFX('vo-fight'), 200) }
    else if (mult <= 0.5) playSFX('not-effective')

    const info = getMatchupLabel(mult)
    setMatchupInfo(info)

    const logLine = `${activeChar.name} used ${move.name}! ${dmg} dmg${mult >= 2 ? ' — Super Effective!' : mult <= 0.5 ? ' — Resisted.' : ''}`
    appendLog(logLine)

    if (nextWildHp <= 0) {
      playSFX('ko')
      playMusic('battle-victory')
      setTimeout(() => playSFX('vo-victory'), 400)
      const xp = Math.round(bounty.bountyValue * 0.6)
      setTimeout(() => onVictory(xp, bounty.bountyValue), 600)
      return
    }

    setTimeout(() => enemyAttack(charHp), 1200)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Turn indicator banner */}
      <div
        className="px-4 py-1.5 text-center border-b border-white border-opacity-5"
        style={{ background: isPlayerTurn && !isAnimating ? 'rgba(0,240,255,0.06)' : 'rgba(255,72,0,0.06)' }}
      >
        <span className={`font-display font-bold text-xs ${isPlayerTurn && !isAnimating ? 'text-ab-gold' : 'text-ab-fire'}`}>
          {isPlayerTurn && !isAnimating ? '⚡ Your Turn' : isAnimating ? '⏳ Resolving...' : '🔴 Enemy Turn'}
        </span>
        {matchupInfo && (
          <span className="ml-3 font-display font-bold text-xs" style={{ color: matchupInfo.color }}>
            {matchupInfo.label}
          </span>
        )}
      </div>

      {/* Pokemon Go-style Battle Arena */}
      <div
        className="relative flex-1 overflow-hidden"
        style={{ background: `linear-gradient(160deg, ${el.color}08, #020510)` }}
      >
        {/* Kenney zone tile strip — decorative background */}
        {(() => {
          const za = ZONE_ASSETS[bounty.element as AssetZoneId] ?? ZONE_ASSETS.ashwake
          return (
            <div className="absolute inset-0 flex items-end justify-center gap-2 pointer-events-none overflow-hidden"
              style={{ ...za.tint, opacity: 0.07 }}>
              {za.tiles.map((tile, i) => (
                <img key={i} src={tile} alt="" height={96}
                  style={{ imageRendering: 'pixelated' }}
                  onError={e => { (e.target as HTMLImageElement).style.display='none' }}
                />
              ))}
            </div>
          )
        })()}

        {/* Opponent Creature (Large, Centered) */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative" style={{ transform: isAttacking ? 'scale(0.95)' : 'scale(1)', transition: 'transform 0.2s' }}>
            {floatDmg && (
              <DamageFloat damage={floatDmg.dmg} color={floatDmg.color} key={floatDmg.key} />
            )}
            <img
              src={CREATURE_PORTRAITS[bounty.creatureName.toLowerCase().replace(/\s+/g, '-')]
                ?? CREATURE_PORTRAITS[bounty.creatureName.toLowerCase()]}
              alt={bounty.creatureName}
              width={200} height={200} className="object-contain"
              style={{ 
                filter: `drop-shadow(0 0 20px ${el.color})`,
                animation: isAttacking ? 'shake 0.3s ease-in-out' : 'none'
              }}
              onError={e => { (e.currentTarget as HTMLImageElement).style.display='none' }}
            />
            {/* Opponent HP Bar */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-48">
              <p className="text-label text-xs text-center mb-1 text-[#EAF6FF]">{bounty.creatureName}</p>
              <HpBar current={wildHp} max={wildMaxHp} color={el.color} />
            </div>
          </div>
        </div>

        {/* Player Character (Back View, Bottom Right) */}
        <div className="absolute bottom-4 right-4 flex flex-col items-center gap-1">
          <StatusBadge status={charStatus} />
          <img 
            src={CHAR_PORTRAITS[activeChar.id]} 
            alt={activeChar.name}
            width={60} height={60} 
            className="object-contain"
            style={{ 
              filter: `drop-shadow(0 0 8px ${charEl.color})`,
              transform: isAttacking ? 'translateX(-10px)' : 'translateX(0)',
              transition: 'transform 0.2s'
            }}
            onError={e => { (e.currentTarget as HTMLImageElement).style.display='none' }}
          />
          {/* Player HP Bar */}
          <div className="w-24">
            <HpBar current={charHp} max={charMaxHp} color={charEl.color} />
          </div>
          <p className="text-[10px] text-[#8A8AA5] font-mono">{activeChar.name}</p>
        </div>
      </div>

      {/* Battle log */}
      <div className="px-4 py-2">
        <TurnLog entries={log} />
      </div>

      {/* Move buttons */}
      <div className="px-4 pb-2">
        <p className="text-label text-xs mb-2">Moves</p>
        <div className="grid grid-cols-1 gap-1.5">
          {activeChar.moves.map((move) => {
            const moveEl = ELEMENTS[move.element]
            const mult = getMatchupMultiplier(move.element as Element, wildCreature.element)
            const matchup = getMatchupLabel(mult)
            const disabled = !isPlayerTurn || isAnimating
            return (
              <button
                key={move.name}
                onClick={() => useMove(move as Move)}
                disabled={disabled}
                className="flex items-center gap-3 p-3 rounded-ab text-left transition-all"
                style={{
                  background: disabled ? 'rgba(255,255,255,0.02)' : `${moveEl.color}10`,
                  border: `1px solid ${disabled ? 'rgba(255,255,255,0.06)' : moveEl.color + '35'}`,
                  opacity: disabled ? 0.5 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
              >
                <span className="text-xl flex-shrink-0">{moveEl.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-black text-sm text-ab-text">{move.name}</p>
                  <p className="text-body text-xs truncate opacity-70">{move.description}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-display font-bold text-xs" style={{ color: moveEl.color }}>PWR {move.power}</p>
                  {matchup && (
                    <p className="font-display font-bold text-[9px] mt-0.5" style={{ color: matchup.color }}>{matchup.label}</p>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Flee button */}
      <div className="px-4 pb-4 pt-1">
        <button
          onClick={() => { playSFX('flee'); onFlee() }}
          disabled={isAnimating}
          className="w-full font-display font-bold text-xs py-2 rounded-ab border border-white border-opacity-10 text-ab-text-dim hover:text-ab-text hover:border-opacity-20 transition-all bg-transparent cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        >
          🏃 Flee
        </button>
      </div>
    </div>
  )
}

// ── Phase: Victory ────────────────────────────────────────────────────────────

function VictoryPhase({
  bounty,
  xpGained,
  coinsGained,
  onAddToVault,
  onClose,
}: {
  bounty: BountyTarget
  xpGained: number
  coinsGained: number
  onAddToVault: () => void
  onClose: () => void
}) {
  const el = ELEMENTS[bounty.element]
  return (
    <div className="flex flex-col items-center justify-center h-full gap-6 p-6 text-center">
      <div className="text-5xl">🏆</div>
      <div>
        <p className="text-label text-xs mb-1" style={{ color: el.color }}>Victory</p>
        <h2 className="font-display font-black text-2xl text-ab-text mb-2">
          {bounty.creatureName} Caught!
        </h2>
        <p className="text-body text-sm">{bounty.species} added to your vault.</p>
      </div>

      <div className="card-base p-4 w-full flex justify-around">
        <div className="text-center">
          <p className="text-label text-xs mb-1">XP Gained</p>
          <p className="font-display font-black text-xl text-ab-text">+{xpGained}</p>
        </div>
        <div className="w-px bg-white opacity-10" />
        <div className="text-center">
          <p className="text-label text-xs mb-1">Coins Gained</p>
          <p className="font-display font-black text-xl text-ab-gold">⚡ {coinsGained}</p>
        </div>
      </div>

      <div className="w-full flex flex-col gap-3">
        <button onClick={onAddToVault} className="btn-primary w-full py-3">
          Add to Vault
        </button>
        <button onClick={onClose} className="btn-ghost w-full py-3">
          Back to Board
        </button>
      </div>
    </div>
  )
}

// ── Phase: Defeat ─────────────────────────────────────────────────────────────

function DefeatPhase({
  bounty,
  onRetry,
  onClose,
}: {
  bounty: BountyTarget
  onRetry: () => void
  onClose: () => void
}) {
  const COOLDOWN_MINUTES = 5
  const [remaining, setRemaining] = useState(COOLDOWN_MINUTES * 60)

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) { clearInterval(interval); return 0 }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const mins = Math.floor(remaining / 60)
  const secs = remaining % 60
  const canRetry = remaining === 0

  return (
    <div className="flex flex-col items-center justify-center h-full gap-6 p-6 text-center">
      <div className="text-5xl">💀</div>
      <div>
        <p className="text-label text-xs mb-1 text-red-400">Defeated</p>
        <h2 className="font-display font-black text-2xl text-ab-text mb-2">
          {bounty.creatureName} escaped.
        </h2>
        <p className="text-body text-sm">Your crew needs to recover.</p>
      </div>

      {!canRetry && (
        <div className="card-base p-4 w-full text-center">
          <p className="text-label text-xs mb-1">Cooldown</p>
          <p className="font-display font-black text-2xl text-ab-text">
            {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
          </p>
        </div>
      )}

      <div className="w-full flex flex-col gap-3">
        <button
          onClick={onRetry}
          disabled={!canRetry}
          className="btn-primary w-full py-3"
          style={{ opacity: canRetry ? 1 : 0.4 }}
        >
          Try Again
        </button>
        <button onClick={onClose} className="btn-ghost w-full py-3">
          Back to Board
        </button>
      </div>
    </div>
  )
}

// ── Main Modal ────────────────────────────────────────────────────────────────

export default function EncounterModal({ bounty, onClose, onVictory }: EncounterModalProps) {
  const { success, error: toastError } = useToast()
  const [phase, setPhase] = useState<EncounterPhase>('crew-select')
  const [crew, setCrew] = useState<Character[]>([])
  const [xpGained, setXpGained] = useState(0)
  const [coinsGained, setCoinsGained] = useState(0)

  useEffect(() => {
    playSFX('modal-open')
    return () => { playMusic('theme-main') }
  }, [])

  const wildCreature = getCreatureForBounty(bounty)

  function handleCrewConfirm(chars: Character[]) {
    setCrew(chars)
    setPhase('battle')
  }

  function handleVictory(xp: number, coins: number, crewTemplateId?: string, turnsUsed?: number) {
    setXpGained(xp)
    setCoinsGained(coins)
    setPhase('victory')
    success(`${bounty.creatureName} caught! +${xp} XP · +${coins} ⚡`)
    BattleAPI.reportEncounter({
      bountyId: bounty.id,
      result: 'victory',
      crewCharacterTemplateId: crewTemplateId,
      turnsUsed,
    }).catch(() => {})
  }

  function handleDefeat(crewTemplateId?: string) {
    toastError(`Defeated by ${bounty.creatureName}. Rest and try again.`)
    setPhase('defeat')
    BattleAPI.reportEncounter({
      bountyId: bounty.id,
      result: 'defeat',
      crewCharacterTemplateId: crewTemplateId,
    }).catch(() => {})
  }

  function handleFlee() {
    toastError(`Fled from ${bounty.creatureName}.`)
    playMusic('theme-main')
    BattleAPI.reportEncounter({ bountyId: bounty.id, result: 'fled' }).catch(() => {})
    onClose()
  }

  function handleAddToVault() {
    onVictory(bounty.id, coinsGained)
    onClose()
  }

  function handleRetry() {
    setPhase('crew-select')
    setCrew([])
  }

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
      style={{ background: 'rgba(2,5,16,0.85)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      {/* Modal panel */}
      <div
        className="w-full md:max-w-md max-h-[92vh] md:max-h-[85vh] flex flex-col overflow-hidden"
        style={{
          background: '#080E1A',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '12px 12px 0 0',
        }}
        // mobile: slides up from bottom; desktop: centered card
      >
        {/* Top bar (non-battle phases only) */}
        {phase !== 'battle' && (
          <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-white border-opacity-5">
            <p className="font-display font-black text-sm text-ab-text uppercase tracking-wider">
              {phase === 'crew-select' && 'Choose Your Crew'}
              {phase === 'victory' && 'Hunt Complete'}
              {phase === 'defeat' && 'Defeated'}
            </p>
            <button
              onClick={onClose}
              className="w-7 h-7 rounded flex items-center justify-center text-ab-text-dim hover:text-ab-text transition-colors bg-transparent border-none cursor-pointer text-lg"
            >
              ✕
            </button>
          </div>
        )}

        {/* Phase content */}
        <div className="flex-1 overflow-y-auto">
          {phase === 'crew-select' && (
            <CrewSelectPhase bounty={bounty} onConfirm={handleCrewConfirm} onClose={onClose} />
          )}

          {phase === 'battle' && wildCreature && (
            <BattlePhase
              crew={crew}
              bounty={bounty}
              wildCreature={wildCreature}
              onVictory={handleVictory}
              onDefeat={handleDefeat}
              onFlee={handleFlee}
            />
          )}

          {phase === 'battle' && !wildCreature && (
            <div className="p-8 text-center">
              <p className="text-body">Creature data not found for this bounty.</p>
              <button onClick={onClose} className="btn-ghost mt-4">Close</button>
            </div>
          )}

          {phase === 'victory' && (
            <VictoryPhase
              bounty={bounty}
              xpGained={xpGained}
              coinsGained={coinsGained}
              onAddToVault={handleAddToVault}
              onClose={onClose}
            />
          )}

          {phase === 'defeat' && (
            <DefeatPhase bounty={bounty} onRetry={handleRetry} onClose={onClose} />
          )}
        </div>
      </div>
    </div>
  )
}
